import { computed, nextTick, ref } from 'vue'
import MonitoringRowRenderer from '@/components/MonitoringRowRenderer.vue'
import MonitoringDetailRenderer from '@/components/MonitoringDetailRenderer.vue'

export function useHomeAgGrid({ monitorings, onShow, onEdit, onLink }) {
    const gridApi = ref(null)

    const requestGridLayout = () => {
        nextTick(() => {
            requestAnimationFrame(() => {
                gridApi.value?.doLayout?.()
                gridApi.value?.sizeColumnsToFit?.()
            })
        })
    }

    let resizeHandlerAttached = false
    const attachGridResizeHandler = () => {
        if (resizeHandlerAttached) return
        window.addEventListener('resize', requestGridLayout)
        resizeHandlerAttached = true
    }

    const cleanupGrid = () => {
        if (resizeHandlerAttached) {
            window.removeEventListener('resize', requestGridLayout)
            resizeHandlerAttached = false
        }
    }

    const onGridReady = (params) => {
        gridApi.value = params.api
        attachGridResizeHandler()
        requestGridLayout()
    }

    const getMonitoringId = (item, fallbackIndex = 0) => {
        if (!item) return String(fallbackIndex)

        switch (item.monitoring_type) {
            case 'quick':
                return String(item.quickMonitoringId ?? item.quick_monitoring?.id ?? item.id ?? fallbackIndex)
            case 'punctual':
                return String(item.punctualMonitoringId ?? item.punctual_monitoring?.id ?? item.id ?? fallbackIndex)
            case 'horizontal':
                return String(item.horizontalMonitoringId ?? item.horizontal_monitoring?.id ?? item.id ?? fallbackIndex)
            default:
                return String(item.id ?? fallbackIndex)
        }
    }

    const hasDegradations = (item) => {
        if (!item?.specificElements) return false
        return item.specificElements.some(el => Array.isArray(el.degradations) && el.degradations.length > 0)
    }

    const expandedMonitoringId = ref(null)

    const closeExpanded = () => {
        expandedMonitoringId.value = null
    }

    const toggleExpandMonitoring = (item) => {
        if (!hasDegradations(item)) return

        const id = getMonitoringId(item)
        expandedMonitoringId.value = expandedMonitoringId.value === id ? null : id

        nextTick(() => {
            gridApi.value?.resetRowHeights?.()
        })
    }

    const gridRowData = computed(() => {
        const rows = []

            ; (monitorings?.data || []).forEach((item, index) => {
                const id = getMonitoringId(item, index)
                const hasDeg = hasDegradations(item)
                const isExpanded = hasDeg && expandedMonitoringId.value === id

                rows.push({
                    __rowType: 'master',
                    __id: `m-${id}`,
                    item,
                    hasDegradations: hasDeg,
                    isExpanded,
                })

                if (isExpanded) {
                    rows.push({
                        __rowType: 'detail',
                        __id: `d-${id}`,
                        item,
                    })
                }
            })

        return rows
    })

    const getRowId = (params) => params.data.__id

    const components = {
        MonitoringRowRenderer,
        MonitoringDetailRenderer,
    }

    const defaultColDef = {
        editable: false,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMenu: true,
    }

    const columnDefs = ref([
        {
            field: '__rowType',
            flex: 1,
            autoHeight: true,
            cellRendererSelector: (params) => {
                if (params.data?.__rowType === 'detail') {
                    return { component: 'MonitoringDetailRenderer' }
                }

                return {
                    component: 'MonitoringRowRenderer',
                    params: {
                        onShow,
                        onEdit,
                        onLink,
                        onToggleExpand: toggleExpandMonitoring,
                    },
                }
            },
        },
    ])

    const afterDataUpdate = () => {
        nextTick(() => {
            gridApi.value?.resetRowHeights?.()
            requestGridLayout()
        })
    }

    return {
        gridApi,
        requestGridLayout,
        cleanupGrid,
        closeExpanded,
        afterDataUpdate,

        gridRowData,
        columnDefs,
        defaultColDef,
        components,
        getRowId,
        onGridReady,
    }
}
